/*
  header-modal-recommendation.js

  Opens the shared product recommendation modal from a header or mega menu link, instead of
  navigating to the search results page.

  Markup contract
  ---------------
  A trigger is any link carrying the nav-modal-trigger class, a query and a modal id. The query
  is still percent encoded, exactly as it appeared in the menu URL:

    <a href="/search?q=blue%20tiger%20eye&modal=1"
       class="navlink navlink--grandchild nav-modal-trigger"
       data-query="blue%20tiger%20eye"
       data-modal="modal-recommendation--header">
      <span class="navtext">Blue Tiger Eye</span>
    </a>

  The class is deliberately NOT trigger-modal-recommendation, the one the gems page uses.
  layout/theme.liquid renders the header well before it loads
  product-recommendation-modal.aio.min.js, so that script's parse time
  querySelectorAll('.trigger-modal-recommendation') would pick these links up as well and fire a
  second, broken request alongside ours. A separate class keeps the two features apart.

  Liquid decides what counts as a trigger, in snippets/nav-modal-query.liquid. Both header
  render paths use it: snippets/nav-item.liquid (desktop mega menu) and
  snippets/nav-item-mobile.liquid (drawer).

  The modal itself is snippets/modal-recommendation.liquid, rendered once from
  layout/theme.liquid with the id modal-recommendation--header. It is rendered outside
  .header__wrapper on purpose: .drawer__content is transformed, which would make it the
  containing block for a position: fixed modal and trap it inside the drawer.

  Deliberately separate from product-recommendation-modal.js
  ----------------------------------------------------------
  That file binds with querySelectorAll at parse time, so it never sees a header re-rendered by
  the theme editor, and it reads dataset off event.target, which for a desktop nav link is the
  inner <span class="navtext"> rather than the anchor. This listens on document and reads off
  the anchor.

  It does not call MicroModal.init(). The single global init lives in
  product-recommendation-modal.js, which is loaded unconditionally and is also what
  snippets/logo-list-modal.liquid depends on. MicroModal.show() works without it.
*/

(function () {
  const selectors = {
    trigger: '.nav-modal-trigger[data-query][data-modal]',
    disclosure: '[data-hover-disclosure]',
    results: '.modal-search-result',
    loader: '.modal-loader',
    section: '#shopify-section-predictive-search',
  };

  const classes = {
    hide: 'hide',
  };

  const ESCAPE = 27;
  const searchUrl = '/search/suggest';
  const searchFields = 'title,product_type,vendor,variants.sku,variants.title';
  const resultsLimit = 8;

  /*
    Close whichever navigation the click came out of, so the shopper is not left with a mega
    menu or a drawer sitting behind the modal.

    Both use the vendor modules' own documented exits rather than reaching into their state:
    HoverDisclosure listens for a keyup ESCAPE on [data-hover-disclosure], and Drawer binds
    hideDrawer to the theme:drawer:close event on document.
  */
  function closeNavigation(trigger) {
    if (trigger.closest(selectors.disclosure)) {
      trigger.dispatchEvent(
        new KeyboardEvent('keyup', {
          bubbles: true,
          key: 'Escape',
          keyCode: ESCAPE,
          which: ESCAPE,
        })
      );
    }

    document.dispatchEvent(new CustomEvent('theme:drawer:close', { bubbles: false }));
  }

  function resetModal(modal) {
    const results = modal.querySelector(selectors.results);
    const loader = modal.querySelector(selectors.loader);

    if (loader) loader.classList.remove(classes.hide);
    if (results) results.innerHTML = '';
  }

  function buildUrl(query) {
    return (
      searchUrl +
      '?q=' +
      encodeURIComponent(query) +
      '&resources[limit]=' +
      resultsLimit +
      '&resources[type]=product' +
      '&resources[options][unavailable_products]=last' +
      '&resources[options][fields]=' +
      searchFields +
      '&section_id=predictive-search'
    );
  }

  /*
    data-query arrives still percent encoded, straight off the menu URL, because Liquid's
    url_decode raises on malformed input and snippets/nav-modal-query.liquid runs in the header
    on every page. Decode here instead, where a mistyped menu URL costs one broken link rather
    than the whole site.
  */
  function decodeQuery(value) {
    try {
      return decodeURIComponent(value.replace(/\+/g, ' '));
    } catch (error) {
      return value;
    }
  }

  /*
    dataset gives back the decoded attribute value, so a translation containing < or & would be
    reinterpreted as markup on the way into innerHTML. Escape it again here.
  */
  function escapeHtml(value) {
    const holder = document.createElement('div');
    holder.textContent = value;
    return holder.innerHTML;
  }

  /*
    Both customer facing strings come off the modal as data attributes, put there by
    snippets/modal-recommendation.liquid, so they go through the locale files rather than being
    hardcoded here. The fallbacks only ever apply to the gems page modals, which do not carry
    the attributes.
  */
  function closeButton(modal) {
    return (
      '<div class="cm-primary-modal-close">' +
      '<a href="#" class="btn btn--large caps btn--primary" data-micromodal-close>' +
      escapeHtml(modal.dataset.closeText || 'Close') +
      '</a>' +
      '</div>'
    );
  }

  function render(modal, markup) {
    const results = modal.querySelector(selectors.results);
    const loader = modal.querySelector(selectors.loader);

    if (loader) loader.classList.add(classes.hide);
    if (results) results.innerHTML = markup;
  }

  function search(modal, query) {
    fetch(buildUrl(query))
      .then(function (response) {
        if (!response.ok) throw new Error(response.status);
        return response.text();
      })
      .then(function (text) {
        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(selectors.section);

        if (!section) throw new Error('Predictive search section missing from response');

        render(modal, section.innerHTML + closeButton(modal));
      })
      .catch(function (error) {
        /*
          Never leave the spinner running. An empty modal with no explanation is worse than a
          link that simply navigated, so say what happened and give a way out.
        */
        console.error('Recommendation modal:', error);
        render(
          modal,
          '<p class="text-center">' +
            escapeHtml(
              modal.dataset.errorText || 'Sorry, we could not load recommendations right now.'
            ) +
            '</p>' +
            closeButton(modal)
        );
      });
  }

  document.addEventListener('click', function (event) {
    if (typeof MicroModal === 'undefined') return;
    if (!event.target || typeof event.target.closest !== 'function') return;

    const trigger = event.target.closest(selectors.trigger);
    if (!trigger) return;

    const query = decodeQuery(trigger.dataset.query).trim();
    const target = trigger.dataset.modal;
    if (query === '') return;

    const modal = document.getElementById(target);
    if (!modal) return;

    event.preventDefault();
    closeNavigation(trigger);

    MicroModal.show(target, {
      awaitCloseAnimation: true,
      onClose: resetModal,
    });

    search(modal, query);
  });
})();
